import React, { useEffect, useState } from "react";

/*
 * Loading placeholders for the dashboard. Each one has the shape of the content it stands in for,
 * so the page keeps its layout while data arrives and nothing jumps when it does. The statistics
 * can take tens of seconds, so their placeholders also show a short message that moves on while
 * the user waits: the page should read as busy, not stuck.
 */

const MESSAGE_STEPS_MS = [5000, 14000];

/** Cycles through `messages` while `active`, advancing at 5 s and 14 s. */
export const useLoadingMessage = (
	active: boolean,
	messages: (string | undefined)[]
): string | undefined => {
	const [step, setStep] = useState(0);

	useEffect(() => {
		setStep(0);
		if (!active) return;
		const timers = MESSAGE_STEPS_MS.map((delay, index) =>
			setTimeout(() => setStep(index + 1), delay)
		);
		return () => timers.forEach(clearTimeout);
	}, [active]);

	return messages[Math.min(step, messages.length - 1)];
};

const Bar = ({ width, height = 12 }: { width: string; height?: number }) => (
	<span className="skeletonBar" style={{ width, height }} />
);

const LoadingMessage = ({ message }: { message?: string }) =>
	message ? (
		<p className="skeletonMessage" role="status" aria-live="polite">
			<span className="skeletonPulseDot" aria-hidden="true" />
			{/* key restarts the fade-in each time the message changes */}
			<span key={message} className="skeletonMessageText">
				{message}
			</span>
		</p>
	) : null;

/** Stand-in for the six status cards (OrderStatus). */
export const StatusCardsSkeleton = ({ message }: { message?: string }) => (
	<div className="statusSkeleton" aria-busy="true">
		<div className="statusSkeletonHeader">
			<Bar width="40%" height={16} />
			<Bar width="20%" />
		</div>
		<div className="statusSkeletonGrid">
			{Array.from({ length: 6 }, (_, index) => (
				<div
					key={index}
					className="statusSkeletonCard"
					style={{ animationDelay: `${index * 80}ms` }}
				>
					<Bar width="60%" />
					<Bar width="30%" height={22} />
					<svg
						className="skeletonSpark"
						viewBox="0 0 100 30"
						preserveAspectRatio="none"
						aria-hidden="true"
					>
						<path d="M0 26 C 20 26, 30 22, 45 16 S 70 4, 100 4" />
					</svg>
				</div>
			))}
		</div>
		<LoadingMessage message={message} />
	</div>
);

/** Stand-in for a chart body: a faint grid with a line that keeps drawing itself. */
export const ChartSkeleton = ({
	message,
	height = 250,
}: {
	message?: string;
	height?: number;
}) => (
	<div className="chartSkeleton" style={{ height }} aria-busy="true">
		<svg
			className="chartSkeletonSvg"
			viewBox="0 0 400 160"
			preserveAspectRatio="none"
			aria-hidden="true"
		>
			{[30, 65, 100, 135].map((y) => (
				<line key={y} x1="0" x2="400" y1={y} y2={y} />
			))}
			<path
				className="chartSkeletonLine"
				pathLength={1}
				d="M0 130 C 40 125, 60 90, 100 95 S 160 120, 200 80 S 270 40, 310 60 S 370 30, 400 25"
			/>
		</svg>
		<LoadingMessage message={message} />
	</div>
);

/** Stand-in for a ProgressListTable: label, value and bar per row. */
export const ListSkeleton = ({
	title,
	rows = 5,
	className = "",
}: {
	title?: string;
	rows?: number;
	className?: string;
}) => (
	<div
		className={`progressListTable listSkeleton ${className}`}
		aria-busy="true"
	>
		{title ? (
			<h1 className="StatisticTitle listSkeletonTitle">{title}</h1>
		) : (
			<Bar width="45%" height={16} />
		)}
		{Array.from({ length: rows }, (_, index) => (
			<div key={index} className="listSkeletonRow">
				<div className="listSkeletonLabels">
					<Bar width={`${50 - (index % 3) * 10}%`} />
					<Bar width="15%" />
				</div>
				<Bar width="100%" height={6} />
			</div>
		))}
	</div>
);

/** Stand-in for the latest-orders cards. */
export const LatestOrdersSkeleton = ({ count }: { count: number }) => (
	<div className="latestOrdersSkeleton" aria-busy="true">
		{Array.from({ length: count }, (_, index) => (
			<div key={index} className="latestOrderSkeletonItem">
				<Bar width="2rem" height={32} />
				<div className="latestOrderSkeletonLines">
					<Bar width="55%" />
					<Bar width="35%" height={16} />
					<Bar width="70%" />
				</div>
			</div>
		))}
	</div>
);

/** A thin bar across the top of the viewport while anything on the page is loading. */
export const TopProgressBar = ({ active }: { active: boolean }) =>
	active ? (
		<div
			className="dashboardProgress"
			role="progressbar"
			aria-hidden="true"
		>
			<span />
		</div>
	) : null;
